import type { WorkflowFormState, WorkflowBuilderStep } from '../pages/admin/WorkflowBuilderTypes.js';

export type WorkflowAssistantPatch = Partial<WorkflowFormState> & {
  addSteps?: Array<Partial<WorkflowBuilderStep>>;
  stepUpdates?: WorkflowStepPatch[];
  updateSteps?: WorkflowStepPatch[];
  removeStepIds?: Array<string | number>;
  removeSteps?: Array<string | number>;
  contractUpdates?: WorkflowContractUpdate[];
  processorProposals?: WorkflowProcessorProposal[];
};

export type WorkflowStepPatch = {
  id?: string | number;
  stepId?: string | number;
  name?: string;
  changes?: Partial<WorkflowBuilderStep>;
} & Partial<WorkflowBuilderStep>;

export interface WorkflowContractUpdate {
  id?: string | number;
  stepId?: string | number;
  name?: string;
  outputContract?: WorkflowBuilderStep['outputContract'];
  inputContracts?: WorkflowBuilderStep['inputContracts'];
  reason?: string;
}

export interface WorkflowProcessorProposal {
  name?: string;
  sourceStep?: string | number;
  sourceSteps?: Array<string | number>;
  targetStep?: string | number;
  type?: 'field_map' | 'merge' | 'filter' | 'aggregate' | 'normalize' | 'llm_transform';
  mode?: 'deterministic' | 'llm';
  mappings?: Array<{ source: string; target: string; label?: string }>;
  conditions?: Array<Record<string, unknown>>;
  groupBy?: string;
  reason?: string;
}

export interface WorkflowAssistantReviewSection {
  id: string;
  title: string;
  detail: string;
  itemCount: number;
  patch: WorkflowAssistantPatch;
  applyable?: boolean;
}

const TOP_LEVEL_FIELDS: Array<keyof WorkflowFormState> = [
  'name',
  'description',
  'category',
  'status',
  'tags',
  'cronSchedule',
  'scheduleEnabled',
  'flowDirection',
];

const PATCH_CONTROL_KEYS = new Set([
  'addSteps',
  'stepUpdates',
  'updateSteps',
  'removeStepIds',
  'removeSteps',
  'contractUpdates',
  'processorProposals',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function stepLabel(step: Partial<WorkflowBuilderStep>, fallback: string): string {
  const name = typeof step.name === 'string' && step.name.trim() ? step.name.trim() : null;
  const template = typeof step.templateSlug === 'string' && step.templateSlug.trim() ? step.templateSlug.trim() : null;
  if (name && template) return `${name} (${template})`;
  return name ?? template ?? fallback;
}

function updateLabel(update: WorkflowStepPatch, index: number): string {
  const ref = update.name ?? update.stepId ?? update.id;
  const changes = isRecord(update.changes)
    ? Object.keys(update.changes)
    : Object.keys(update).filter((key) => !['id', 'stepId', 'name', 'changes'].includes(key));
  const target = ref !== undefined && ref !== null && ref !== '' ? String(ref) : `step ${index + 1}`;
  return changes.length > 0 ? `${target}: ${changes.join(', ')}` : target;
}

function joinPreview(values: string[]): string {
  if (values.length === 0) return '';
  if (values.length <= 3) return values.join(', ');
  return `${values.slice(0, 3).join(', ')} +${values.length - 3} more`;
}

export function buildWorkflowAssistantReviewSections(patch: Record<string, unknown>): WorkflowAssistantReviewSection[] {
  const sections: WorkflowAssistantReviewSection[] = [];

  const workflowFields: Record<string, unknown> = {};
  for (const field of TOP_LEVEL_FIELDS) {
    if (field in patch && patch[field] !== undefined) workflowFields[field] = patch[field];
  }
  const workflowFieldNames = Object.keys(workflowFields);
  if (workflowFieldNames.length > 0) {
    sections.push({
      id: 'workflow-fields',
      title: 'Workflow fields',
      detail: workflowFieldNames.join(', '),
      itemCount: workflowFieldNames.length,
      patch: workflowFields as WorkflowAssistantPatch,
    });
  }

  const addSteps = Array.isArray(patch.addSteps) ? (patch.addSteps as Array<Partial<WorkflowBuilderStep>>) : [];
  if (addSteps.length > 0) {
    sections.push({
      id: 'add-steps',
      title: 'Add steps',
      detail: joinPreview(addSteps.map((step, index) => stepLabel(step, `step ${index + 1}`))),
      itemCount: addSteps.length,
      patch: { addSteps },
    });
  }

  const stepUpdates = [
    ...(Array.isArray(patch.stepUpdates) ? (patch.stepUpdates as WorkflowStepPatch[]) : []),
    ...(Array.isArray(patch.updateSteps) ? (patch.updateSteps as WorkflowStepPatch[]) : []),
  ];
  if (stepUpdates.length > 0) {
    sections.push({
      id: 'update-steps',
      title: 'Update steps',
      detail: joinPreview(stepUpdates.map(updateLabel)),
      itemCount: stepUpdates.length,
      patch: { stepUpdates },
    });
  }

  const contractUpdates = Array.isArray(patch.contractUpdates)
    ? (patch.contractUpdates as WorkflowContractUpdate[])
    : [];
  if (contractUpdates.length > 0) {
    sections.push({
      id: 'contract-updates',
      title: 'Update contracts',
      detail: joinPreview(
        contractUpdates.map((update, index) =>
          String(update.name ?? update.stepId ?? update.id ?? `step ${index + 1}`),
        ),
      ),
      itemCount: contractUpdates.length,
      patch: { contractUpdates },
    });
  }

  const processorProposals = Array.isArray(patch.processorProposals)
    ? (patch.processorProposals as WorkflowProcessorProposal[])
    : [];
  if (processorProposals.length > 0) {
    const deterministic = processorProposals.filter(
      (proposal) => proposal.type !== 'llm_transform' && proposal.mode !== 'llm',
    );
    const llmTransforms = processorProposals.filter(
      (proposal) => proposal.type === 'llm_transform' || proposal.mode === 'llm',
    );
    if (deterministic.length > 0) {
      sections.push({
        id: 'processor-proposals',
        title: 'Add deterministic processors',
        detail: joinPreview(deterministic.map(processorProposalLabel)),
        itemCount: deterministic.length,
        patch: { processorProposals: deterministic },
      });
    }
    if (llmTransforms.length > 0) {
      sections.push({
        id: 'llm-transform-proposals',
        title: 'Add LLM transform steps',
        detail: joinPreview(llmTransforms.map(processorProposalLabel)),
        itemCount: llmTransforms.length,
        patch: { processorProposals: llmTransforms },
        applyable: true,
      });
    }
  }

  function processorProposalLabel(proposal: WorkflowProcessorProposal): string {
    if (proposal.name) return proposal.name;
    const source =
      Array.isArray(proposal.sourceSteps) && proposal.sourceSteps.length > 0
        ? proposal.sourceSteps.map(String).join(', ')
        : proposal.sourceStep
          ? String(proposal.sourceStep)
          : 'source';
    const target = proposal.targetStep ? String(proposal.targetStep) : 'target';
    const type = proposal.type ? proposal.type.replace('_', ' ') : 'processor';
    return `${type}: ${source} -> ${target}`;
  }

  const removeRefs = [
    ...(Array.isArray(patch.removeStepIds) ? (patch.removeStepIds as Array<string | number>) : []),
    ...(Array.isArray(patch.removeSteps) ? (patch.removeSteps as Array<string | number>) : []),
  ];
  if (removeRefs.length > 0) {
    sections.push({
      id: 'remove-steps',
      title: 'Remove steps',
      detail: removeRefs.map(String).join(', '),
      itemCount: removeRefs.length,
      patch: { removeStepIds: removeRefs },
    });
  }

  const knownKeys = new Set([...TOP_LEVEL_FIELDS.map(String), ...PATCH_CONTROL_KEYS]);
  const otherPatch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (!knownKeys.has(key) && value !== undefined) otherPatch[key] = value;
  }
  const otherKeys = Object.keys(otherPatch);
  if (otherKeys.length > 0) {
    sections.push({
      id: 'other-workflow-fields',
      title: 'Other workflow changes',
      detail: otherKeys.join(', '),
      itemCount: otherKeys.length,
      patch: otherPatch as WorkflowAssistantPatch,
    });
  }

  return sections;
}

export function selectWorkflowAssistantPatch(
  sections: WorkflowAssistantReviewSection[],
  selectedSectionIds: Set<string>,
): WorkflowAssistantPatch {
  const selected: WorkflowAssistantPatch = {};
  for (const section of sections) {
    if (!selectedSectionIds.has(section.id)) continue;
    for (const [key, value] of Object.entries(section.patch)) {
      if (Array.isArray(value) && Array.isArray((selected as Record<string, unknown>)[key])) {
        (selected as Record<string, unknown>)[key] = [
          ...((selected as Record<string, unknown>)[key] as unknown[]),
          ...value,
        ];
      } else {
        (selected as Record<string, unknown>)[key] = value;
      }
    }
  }
  return selected;
}
